// Monotonic invalidation counters, one per RTK Query cache tag (optionally per tag + pipeline
// configuration scope), bumped whenever a data-change notification invalidates that tag (see
// useDataChangeInvalidator).
//
// RTK Query refetches its own cached entries when a tag is invalidated, so almost every consumer
// needs nothing more. Custom dashboard view code is the exception: it fetches KPI data imperatively
// from inside the sandboxed view, outside the query cache, so an invalidation has no entry to
// refresh. Those consumers read the tag's counter instead and re-run their fetches when it moves —
// which is how a completed gold pipeline run, or a push to a team's connected repo, reaches a
// custom view.
//
// Scoped bumps carry the pipeline configuration a change belongs to (e.g. a branch's gold run
// completing), so consumers bound to one configuration ignore the other's changes. Unscoped bumps
// (a reconnect, a notification from a server that predates scoping) move the bare tag counter,
// which every consumer of the tag observes regardless of scope.
//
// Module-level rather than a Redux slice so every app that uses the shared invalidator gets it with
// no store wiring, the same way SignalRService keeps its handler sets here.

const versionsByKey = new Map<string, number>();
const listeners = new Set<() => void>();

const versionKey = (tag: string, scope?: string | null): string => (scope ? `${tag}::${scope}` : tag);

/** Bumps the counter for each tag (scoped when `scope` is given) and notifies subscribers. */
export function bumpDataChangeVersions(tags: readonly string[], scope?: string | null): void {
  if (tags.length === 0) {
    return;
  }

  tags.forEach(tag => {
    const key = versionKey(tag, scope);
    versionsByKey.set(key, (versionsByKey.get(key) ?? 0) + 1);
  });
  listeners.forEach(listener => listener());
}

/**
 * The number of invalidations seen for a tag in this session — the unscoped count plus, when a
 * scope is given, the count for that scope. Zero until the first one.
 */
export function getDataChangeVersion(tag: string, scope?: string): number {
  const bare = versionsByKey.get(tag) ?? 0;
  if (!scope) {
    return bare;
  }
  return bare + (versionsByKey.get(versionKey(tag, scope)) ?? 0);
}

export function subscribeToDataChangeVersions(listener: () => void): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}
